import { useField, useFormikContext } from 'formik';
import React, { createContext, useContext, useState } from 'react';

import styled from '@emotion/styled';

import { FieldErrorInfo, InputLabel, InputWrapper } from './FormField';

interface BaseProps {
  label?: string;
  id?: string;
  className?: string;
}

interface FormikProps extends BaseProps {
  name: string;
  checked?: never;
  onChange?: (checked: boolean) => void;
}

interface StandaloneProps extends BaseProps {
  name?: never;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  defaultChecked?: boolean;
}

export type ToggleButtonFieldProps = FormikProps | StandaloneProps;

// Formik-connected version — must be its own component so useField is never called conditionally
const FormikToggleButton: React.FC<FormikProps> = ({
  label,
  id,
  className,
  name,
  onChange,
}) => {
  const [field, meta] = useField({ name, type: 'checkbox' });

  return (
    <InputWrapper className={className}>
      {label && <InputLabel htmlFor={id ?? name}>{label}</InputLabel>}
      <SliderArea>
        <Slider />
        <Checkbox
          id={id ?? name}
          type='checkbox'
          {...field}
          onChange={(e) => {
            field.onChange(e);
            onChange?.(e.target.checked);
          }}
        />
      </SliderArea>
      {meta.touched && meta.error ? (
        <FieldErrorInfo>{meta.error}</FieldErrorInfo>
      ) : null}
    </InputWrapper>
  );
};

// Standalone version — controlled when checked/onChange are provided, uncontrolled otherwise
const StandaloneToggleButton: React.FC<StandaloneProps> = ({
  label,
  id,
  className,
  checked,
  onChange,
  defaultChecked = false,
}) => {
  const [internalChecked, setInternalChecked] = useState(defaultChecked);
  const isControlled = checked !== undefined;
  const value = isControlled ? checked : internalChecked;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isControlled) setInternalChecked(e.target.checked);
    onChange?.(e.target.checked);
  };

  return (
    <InputWrapper className={className}>
      {label && <InputLabel htmlFor={id}>{label}</InputLabel>}
      <SliderArea>
        <Slider />
        <Checkbox
          id={id}
          type='checkbox'
          checked={value}
          onChange={handleChange}
        />
      </SliderArea>
    </InputWrapper>
  );
};

const ToggleButtonField: React.FC<ToggleButtonFieldProps> = (props) => {
  if (props.name !== undefined) {
    return <FormikToggleButton {...(props as FormikProps)} />;
  }
  return <StandaloneToggleButton {...(props as StandaloneProps)} />;
};

export default ToggleButtonField;

// ─── Toggle Group (multi-select) ──────────────────────────────────────────────

interface ToggleGroupCtx {
  name?: string;
  selectedValues: string[];
  toggle: (value: string, checked: boolean) => void;
}

const ToggleGroupContext = createContext<ToggleGroupCtx | null>(null);

// ToggleItem — Formik variant (array checkbox)
const FormikToggleItem: React.FC<{
  name: string;
  value: string;
  label?: string;
  id?: string;
  className?: string;
}> = ({ name, value, label, id, className }) => {
  const [field] = useField({ name, type: 'checkbox', value });
  return (
    <ToggleItemWrapper className={className}>
      <SliderArea>
        <Slider />
        <Checkbox id={id} type='checkbox' {...field} value={value} />
      </SliderArea>
      {label && <ToggleItemLabel htmlFor={id}>{label}</ToggleItemLabel>}
    </ToggleItemWrapper>
  );
};

// ToggleItem — Standalone variant
const StandaloneToggleItem: React.FC<{
  value: string;
  label?: string;
  id?: string;
  className?: string;
}> = ({ value, label, id, className }) => {
  const ctx = useContext(ToggleGroupContext)!;
  const checked = ctx.selectedValues.includes(value);
  return (
    <ToggleItemWrapper className={className}>
      <SliderArea>
        <Slider />
        <Checkbox
          id={id}
          type='checkbox'
          checked={checked}
          onChange={(e) => ctx.toggle(value, e.target.checked)}
        />
      </SliderArea>
      {label && <ToggleItemLabel htmlFor={id}>{label}</ToggleItemLabel>}
    </ToggleItemWrapper>
  );
};

export interface ToggleItemProps {
  value: string;
  label?: string;
  id?: string;
  className?: string;
}

export const ToggleItem: React.FC<ToggleItemProps> = (props) => {
  const ctx = useContext(ToggleGroupContext);
  if (ctx?.name) return <FormikToggleItem name={ctx.name} {...props} />;
  return <StandaloneToggleItem {...props} />;
};

// ToggleGroup — Formik variant
interface ToggleGroupFormikProps {
  name: string;
  label?: string;
  className?: string;
  children: React.ReactNode;
}

const FormikToggleGroup: React.FC<ToggleGroupFormikProps> = ({
  name,
  label,
  className,
  children,
}) => {
  const { errors, touched } = useFormikContext<any>();
  return (
    <ToggleGroupContext.Provider
      value={{ name, selectedValues: [], toggle: () => {} }}
    >
      <InputWrapper className={className}>
        {label && <InputLabel>{label}</InputLabel>}
        <GroupItems>{children}</GroupItems>
        {touched[name] && errors[name] ? (
          <FieldErrorInfo>{(errors as any)[name]}</FieldErrorInfo>
        ) : null}
      </InputWrapper>
    </ToggleGroupContext.Provider>
  );
};

// ToggleGroup — Standalone variant
interface ToggleGroupStandaloneProps {
  name?: never;
  label?: string;
  className?: string;
  children: React.ReactNode;
  value?: string[];
  onChange?: (values: string[]) => void;
  defaultValue?: string[];
}

const StandaloneToggleGroup: React.FC<ToggleGroupStandaloneProps> = ({
  label,
  className,
  children,
  value,
  onChange,
  defaultValue = [],
}) => {
  const [internal, setInternal] = useState<string[]>(defaultValue);
  const isControlled = value !== undefined;
  const selected = isControlled ? value! : internal;

  const toggle = (v: string, checked: boolean) => {
    const next = checked ? [...selected, v] : selected.filter((x) => x !== v);
    if (!isControlled) setInternal(next);
    onChange?.(next);
  };

  return (
    <ToggleGroupContext.Provider value={{ selectedValues: selected, toggle }}>
      <InputWrapper className={className}>
        {label && <InputLabel>{label}</InputLabel>}
        <GroupItems>{children}</GroupItems>
      </InputWrapper>
    </ToggleGroupContext.Provider>
  );
};

export type ToggleGroupProps =
  | ToggleGroupFormikProps
  | ToggleGroupStandaloneProps;

export const ToggleGroup: React.FC<ToggleGroupProps> = (props) => {
  if (props.name !== undefined)
    return <FormikToggleGroup {...(props as ToggleGroupFormikProps)} />;
  return <StandaloneToggleGroup {...(props as ToggleGroupStandaloneProps)} />;
};

const SliderArea = styled.div`
  width: 46px;
  height: 20px;
  border-radius: 15px;
  padding: 1.7px;
  background-color: rgba(110, 110, 117, 0.3);
  transition: background-color 0.3s ease-in-out;
  align-items: center;
  display: grid;

  & > * {
    grid-column: 1;
    grid-row: 1;
  }

  &:has(input[type='checkbox']:checked) {
    background-color: #5b91f5;
  }

  &:has(input[type='checkbox']:checked) > div {
    transform: translateX(15.6px);
  }
`;

const Slider = styled.div`
  width: 27px;
  height: 17px;
  background-color: white;
  border-radius: 9px;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
`;

// make the checkbox invisible but still clickable
const Checkbox = styled.input`
  opacity: 0;
`;

const ToggleItemWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ToggleItemLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
`;

const GroupItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
